import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ErrorDisplayProps {
  error: string;
  onRetry?: () => void;
  title?: string;
  description?: string | null;
  showRetry?: boolean;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  title = 'Error',
  description = null,
  showRetry = true,
}) => {
  const navigate = useNavigate();

  // Detect if this is an AWS environment configuration error
  const isAwsEnvironmentError =
    error &&
    (error.toLowerCase().includes('cost analytics') ||
      error.toLowerCase().includes('monthly spend') ||
      error.toLowerCase().includes('finops') ||
      error.toLowerCase().includes('cost data'));

  const handleConfigureEnvironments = () => {
    navigate('/hub/aws-environments');
  };

  if (isAwsEnvironmentError) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200">
                AWS Environment Configuration Required
              </h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                <p>To view cost analytics and financial data, you need to configure your AWS environment connection.</p>
                <p className="mt-2">
                  This allows Aivilo Lens to securely access your AWS cost and usage data through cross-account role
                  assumption.
                </p>
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleConfigureEnvironments}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Configure AWS Environments
                </button>
                {showRetry && onRetry && (
                  <button
                    onClick={onRetry}
                    className="bg-blue-100 dark:bg-blue-800 px-4 py-2 rounded-md text-sm font-medium text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-700 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{title}</h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
              <p>{error}</p>
              {description && <p className="mt-1">{description}</p>}
            </div>
            {showRetry && onRetry && (
              <div className="mt-4">
                <button
                  onClick={onRetry}
                  className="bg-red-100 dark:bg-red-800 px-3 py-2 rounded-md text-sm font-medium text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;
